import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import PasswordStrengthBar from './PasswordStrengthBar';

const InviteForm = ({ email, inviteData, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    fullName: inviteData?.fullName || '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (!/[A-Z]/?.test(formData?.password) || !/[a-z]/?.test(formData?.password)) {
      newErrors.password = 'Password must contain uppercase and lowercase letters';
    } else if (!/[0-9]/?.test(formData?.password)) {
      newErrors.password = 'Password must contain at least one number';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Email (read-only) */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">
          Email Address
        </label>
        <div className="flex items-center gap-2 px-3 py-2.5 bg-muted/50 border border-border rounded-md">
          <Icon name="Mail" size={16} className="text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-muted-foreground">{email}</span>
          <Icon name="Lock" size={14} className="text-muted-foreground ml-auto flex-shrink-0" />
        </div>
        <p className="text-xs text-muted-foreground mt-1">Email address from your invitation</p>
      </div>

      {/* Role info */}
      {inviteData?.role && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-accent/10 border border-accent/20 rounded-md">
          <Icon name="Shield" size={16} className="text-accent flex-shrink-0" />
          <span className="text-sm text-foreground">
            You'll be assigned the <span className="font-semibold capitalize">{inviteData?.role}</span> role
          </span>
        </div>
      )}

      {/* Full Name */}
      <Input
        type="text"
        name="fullName"
        label="Full Name"
        placeholder="Enter your full name"
        value={formData?.fullName}
        onChange={handleChange}
        error={errors?.fullName}
        required
        autoComplete="name"
        disabled={loading}
        autoFocus
      />

      {/* Password */}
      <div className="relative">
        <Input
          type={showPassword ? 'text' : 'password'}
          name="password"
          label="Password"
          placeholder="Create a password"
          value={formData?.password}
          onChange={handleChange}
          error={errors?.password}
          required
          autoComplete="new-password"
          disabled={loading}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
          disabled={loading}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
        </button>
        <PasswordStrengthBar password={formData?.password} />
      </div>

      {/* Confirm Password */}
      <div className="relative">
        <Input
          type={showConfirmPassword ? 'text' : 'password'}
          name="confirmPassword"
          label="Confirm Password"
          placeholder="Confirm your password"
          value={formData?.confirmPassword}
          onChange={handleChange}
          error={errors?.confirmPassword}
          required
          autoComplete="new-password"
          disabled={loading}
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
          disabled={loading}
          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
        >
          <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
        </button>
      </div>

      <Button
        type="submit"
        fullWidth
        loading={loading}
        disabled={loading}
        className="mt-6"
      >
        {loading ? 'Setting up your account...' : 'Complete Setup'}
      </Button>
    </form>
  );
};

export default InviteForm;
